import {
  DEFAULT_ACCOUNT_LOCALE,
  SUPPORTED_ACCOUNT_LOCALE_CODES,
  isAccountLocale,
  type AccountLocale,
} from "@kaleo/account-localization";

export const MDX_ROUTE_SUFFIXES = [
  "welcome",
  "getting-started",
  "getting-started/set-up-your-account",
  "getting-started/set-up-your-audio-input",
  "getting-started/start-translating",
  "getting-started/share-with-your-church",
  "supported-languages",
  "how-to/download-past-translations",
  "how-to/customize-the-translation",
  "integrations/mcp-server",
  "integrations/api",
  "api/languages/special-language-values",
] as const;

export const API_ROUTE_SUFFIXES = [
  "api",
  "api/languages",
  "api/devices",
  "api/streaming",
  "api/sessions",
  "api/schedule",
  "api/~schemas",
] as const;
export const DOCS_ROUTE_SUFFIXES = [
  ...MDX_ROUTE_SUFFIXES,
  ...API_ROUTE_SUFFIXES,
] as const;

export type DocsRouteSuffix = (typeof DOCS_ROUTE_SUFFIXES)[number];

export const LEGACY_DOCS_ROUTE_REDIRECTS = {
  introduction: "welcome",
  "integrations/kaleo-api": "integrations/api",
} as const satisfies Record<string, DocsRouteSuffix>;

const docsRouteSuffixSet = new Set<string>(DOCS_ROUTE_SUFFIXES);
const publishedExtensions = [".html", ".md"] as const;

export const DOCS_LOCALES = SUPPORTED_ACCOUNT_LOCALE_CODES;
export const DEFAULT_DOCS_LOCALE = DEFAULT_ACCOUNT_LOCALE;
export const DEFAULT_DOCS_ROUTE_SUFFIX: DocsRouteSuffix = "welcome";
export const DOCS_ROUTE_LOCALE_COOKIE_NAME = "babel.docs.route-locale";

export function isDocsRouteSuffix(value: unknown): value is DocsRouteSuffix {
  return typeof value === "string" && docsRouteSuffixSet.has(value);
}

export function parseLocalizedDocsPath(pathname: string): {
  locale: AccountLocale;
  suffix: DocsRouteSuffix | null;
} {
  const segments = pathname.split("/").filter(Boolean);
  const candidateLocale = segments[0];
  const locale = isAccountLocale(candidateLocale)
    ? candidateLocale
    : DEFAULT_DOCS_LOCALE;
  const candidateSuffix = stripPublishedExtension(segments.slice(1).join("/"));

  return {
    locale,
    suffix: isDocsRouteSuffix(candidateSuffix) ? candidateSuffix : null,
  };
}

function stripPublishedExtension(value: string): string {
  const extension = publishedExtensions.find((candidate) =>
    value.endsWith(candidate),
  );
  return extension ? value.slice(0, -extension.length) : value;
}

export function getLocalizedDocsPath(
  locale: AccountLocale,
  suffix: DocsRouteSuffix = DEFAULT_DOCS_ROUTE_SUFFIX,
): string {
  return `/${locale}/${suffix}`;
}

export function replaceDocsPathLocale(
  pathname: string,
  locale: AccountLocale,
): string {
  const { suffix } = parseLocalizedDocsPath(pathname);
  return getLocalizedDocsPath(locale, suffix ?? DEFAULT_DOCS_ROUTE_SUFFIX);
}

export function replaceDocsLocationLocale(
  location: {
    pathname: string;
    search: string;
    hash: string;
  },
  locale: AccountLocale,
): string {
  return `${replaceDocsPathLocale(location.pathname, locale)}${location.search}${location.hash}`;
}
